import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ChatCenteredText, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";

function fmtDT(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString(undefined, { hour12: false });
}

export default function MessagesPanel({ vesselId }) {
    const { user } = useAuth();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newBody, setNewBody] = useState("");
    const [replyFor, setReplyFor] = useState(null);
    const [replyBody, setReplyBody] = useState("");
    const [posting, setPosting] = useState(false);

    const canCompose = ["admin", "manager", "supervisor", "finance", "safety", "shift_millwright"].includes(user?.role);

    const load = async () => {
        try {
            const { data } = await api.get(`/vessels/${vesselId}/messages`);
            setMessages(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 20000); // poll every 20s
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vesselId]);

    const sendMessage = async (e) => {
        e.preventDefault();
        if (!newBody.trim()) return;
        setPosting(true);
        try {
            await api.post(`/vessels/${vesselId}/messages`, { body: newBody });
            setNewBody("");
            toast.success("Feedback requested");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setPosting(false);
        }
    };

    const sendReply = async (messageId) => {
        if (!replyBody.trim()) return;
        setPosting(true);
        try {
            await api.post(`/vessels/${vesselId}/messages/${messageId}/reply`, { body: replyBody });
            setReplyBody("");
            setReplyFor(null);
            toast.success("Reply sent");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setPosting(false);
        }
    };

    const resolve = async (messageId) => {
        try {
            await api.post(`/vessels/${vesselId}/messages/${messageId}/resolve`);
            toast.success("Marked resolved");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div className="g-card shadow-sm" data-testid="messages-panel">
            <div className="g-card-header-dark">
                <ChatCenteredText size={18} />
                <div className="font-display font-bold uppercase tracking-wide">Feedback & Messages</div>
                <span className="ml-auto text-xs font-semibold">{messages.length} thread{messages.length === 1 ? "" : "s"}</span>
            </div>

            {canCompose && (
                <form onSubmit={sendMessage} className="p-4 border-b border-slate-100 bg-[var(--surface-alt)]" data-testid="compose-form">
                    <label className="uppercase-label block mb-1.5 text-[var(--grindrod-red)]">
                        Request feedback from supervisor
                    </label>
                    <div className="flex gap-2 flex-col md:flex-row">
                        <textarea
                            value={newBody}
                            onChange={(e) => setNewBody(e.target.value)}
                            rows={2}
                            placeholder="e.g. Why did the flow rate drop between 14:00 and 16:00?"
                            className="g-input flex-1"
                            data-testid="compose-body"
                        />
                        <button
                            type="submit"
                            disabled={posting || !newBody.trim()}
                            className="btn-primary inline-flex items-center gap-2 self-start"
                            data-testid="compose-send"
                        >
                            <PaperPlaneTilt size={14} /> Send
                        </button>
                    </div>
                </form>
            )}

            {loading ? (
                <div className="p-6 text-sm text-slate-400 text-center" data-testid="messages-loading">Loading messages…</div>
            ) : messages.length === 0 ? (
                <div className="p-6 text-sm text-slate-400 text-center" data-testid="messages-empty">
                    No feedback requests or messages yet.
                </div>
            ) : (
                <ul className="divide-y divide-slate-100">
                    {messages.map((m) => (
                        <li key={m.id} className="p-4" data-testid={`message-${m.id}`}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-semibold text-[var(--grindrod-navy)]">{m.from_name}</span>
                                        <span className={`g-pill ${m.from_role === "manager" ? "g-pill-navy" : m.from_role === "admin" ? "g-pill-red" : "g-pill-green"}`}>
                                            {m.from_role}
                                        </span>
                                        {m.source === "millwright_auto" && (
                                            <span
                                                className="g-pill"
                                                style={{ background: "#FEF3C7", color: "#B45309", border: "1px solid #FCD34D", fontSize: 9, letterSpacing: "0.05em" }}
                                                title="Auto-posted from the millwright breakdown flow"
                                                data-testid={`msg-source-badge-${m.id}`}
                                            >
                                                ⚙ Maintenance auto-update
                                            </span>
                                        )}
                                        {m.resolved_at && (
                                            <span className="g-pill g-pill-green" title={`Resolved at ${fmtDT(m.resolved_at)}`}>
                                                <CheckCircle size={10} weight="fill" /> Resolved
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-slate-500 mt-0.5">{fmtDT(m.created_at)}</div>
                                </div>
                                {!m.resolved_at && (
                                    <button
                                        onClick={() => resolve(m.id)}
                                        className="text-xs text-[var(--grindrod-navy)] hover:text-[var(--grindrod-red)] font-semibold"
                                        data-testid={`resolve-${m.id}`}
                                    >
                                        Mark resolved
                                    </button>
                                )}
                            </div>
                            <div className="mt-2 text-sm text-slate-700 whitespace-pre-wrap">{m.body}</div>

                            {m.replies?.length > 0 && (
                                <ul className="mt-3 space-y-2 pl-4 border-l-2 border-[var(--grindrod-red)]">
                                    {m.replies.map((r) => (
                                        <li key={r.id || r.created_at} className="text-sm" data-testid={`reply-${r.id || r.created_at}`}>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-semibold text-[var(--grindrod-navy)]">{r.from_name}</span>
                                                <span className={`g-pill ${r.from_role === "manager" ? "g-pill-navy" : r.from_role === "admin" ? "g-pill-red" : "g-pill-green"}`} style={{ fontSize: 9, padding: "1px 6px" }}>
                                                    {r.from_role}
                                                </span>
                                                <span className="text-xs text-slate-400">{fmtDT(r.created_at)}</span>
                                            </div>
                                            <div className="text-slate-700 mt-0.5 whitespace-pre-wrap">{r.body}</div>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            {!m.resolved_at && replyFor !== m.id && (
                                <button
                                    onClick={() => {
                                        setReplyFor(m.id);
                                        setReplyBody("");
                                    }}
                                    className="mt-3 text-xs font-semibold text-[var(--grindrod-red)] hover:underline"
                                    data-testid={`reply-btn-${m.id}`}
                                >
                                    Reply
                                </button>
                            )}

                            {replyFor === m.id && (
                                <div className="mt-3 flex gap-2 flex-col md:flex-row">
                                    <textarea
                                        value={replyBody}
                                        onChange={(e) => setReplyBody(e.target.value)}
                                        rows={2}
                                        placeholder="Type your response…"
                                        className="g-input flex-1"
                                        data-testid={`reply-input-${m.id}`}
                                    />
                                    <div className="flex gap-2 self-start">
                                        <button onClick={() => setReplyFor(null)} className="btn-outline !py-2 !px-3 text-xs">
                                            Cancel
                                        </button>
                                        <button
                                            onClick={() => sendReply(m.id)}
                                            disabled={posting || !replyBody.trim()}
                                            className="btn-primary !py-2 !px-3 text-xs inline-flex items-center gap-1"
                                            data-testid={`reply-send-${m.id}`}
                                        >
                                            <PaperPlaneTilt size={12} /> Send reply
                                        </button>
                                    </div>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
